import { useGetAiWorkersByCategoryQuery, useTypedSelector, categoryType, useDeleteAiWorkersMutation, RouteState, useEntityResourcePermissions, INSIGHT_AGENT_SUB_TYPE } from '@circuitry-ai/doc-data';
import {
  CardListViewMode,
  EntityCardWithActions,
  EntityListView,
  ListActionsCellWithDelete,
  checkType,
  CtyButton,
  CtyShowToast,
  getActionsColumnDef,
  getDescriptionColumnDef,
  getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { Chip } from '@nextui-org/react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AnalystTabs } from './analystBoard';
import {
  ANALYST_RUNTIMES,
  analystRoute,
  isInsightAgentWorker,
  isInsightDashboardWorker,
  linkedInsightAgentIds,
  listedDashboardId,
  useAnalystInsight,
  type AnalystInsight,
  type AnalystWorkerLike,
} from './analystRuntime';

/** An aiworker as the list shows it. */
type ListedAgent = AnalystWorkerLike & { readonly description?: string };

/** The list's stand-in for the tenant's default Insight Agent until someone customises it. */
export const DEFAULT_INSIGHT_AGENT_ITEM_ID = 'insight-agent-default';

const isDefaultStandIn = (report: ListedAgent | null | undefined) => report?.aiworker_id === DEFAULT_INSIGHT_AGENT_ITEM_ID;

function buildReportUrl(report: any, tabValue?: string): string {
  const reportID = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'report_id')?.attr_value;
  const analystType = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'analyst_type')?.attr_value;
  const reportType = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'report_type')?.attr_value;
  const aiworkerId = report.aiworker_id;
  const tab = tabValue ?? (analystType === 'prescriptive' ? AnalystTabs.CLAIM_SCORE : AnalystTabs.DASHBOARD);
  const params = new URLSearchParams({ tab, aiworkerId });
  if (reportID) params.append('reportId', reportID);
  if (analystType === 'evaluations' || analystType === 'decision' || analystType === 'pyanalyst') {
    params.append('analystType', analystType);
    if (analystType === 'evaluations' && reportType) params.append('reportType', reportType);
  }
  return `/analytics/${RouteState.VIEW}?${params.toString()}`;
}

/** A listed dashboard opens its own page (Launch and Edit alike: its owner edits it there). */
const dashboardPage = (report: ListedAgent, insight: AnalystInsight | null): string | null =>
  (insight?.dashboardUrl && isInsightDashboardWorker(report) ? insight.dashboardUrl(listedDashboardId(report)) : null);

/**
 * Where Launch goes. An Insight agent opens the Insight conversation with it; so does an analyst
 * switched to the Insight runtime, with its linked agent; a shared dashboard opens its page. Without
 * AI Insight (`insight` null) every analyst opens its own dashboard, as before.
 */
export function launchUrl(report: ListedAgent, insight: AnalystInsight | null): string {
  const dashboard = dashboardPage(report, insight);
  if (dashboard) return dashboard;
  if (insight && isInsightAgentWorker(report)) {
    return isDefaultStandIn(report) ? insight.conversationUrl() : insight.conversationUrl(report.aiworker_id ?? '');
  }
  const route = analystRoute(report);
  if (insight && route.runtime === ANALYST_RUNTIMES.INSIGHT) return insight.conversationUrl(route.agentId);
  return buildReportUrl(report);
}

/** Where Edit goes: the Insight agent editor, a dashboard's page, or the analyst's Configuration tab (where its runtime is switched). */
export function editUrl(report: ListedAgent, insight: AnalystInsight | null): string {
  const dashboard = dashboardPage(report, insight);
  if (dashboard) return dashboard;
  if (insight && isInsightAgentWorker(report)) {
    return isDefaultStandIn(report) ? insight.customiseDefaultUrl : insight.editorUrl(report.aiworker_id ?? '');
  }
  return buildReportUrl(report, AnalystTabs.CONFIGURATION);
}

/** An analyst page gets the aiworker as router state; AI Insight's pages load their own. */
const navigateState = (report: ListedAgent) =>
  (isInsightAgentWorker(report) || isInsightDashboardWorker(report) ? undefined : { state: { report } });

/**
 * The stand-in has no FGA resource: anyone on this page may ask it, and whoever may create
 * analysts may customise it. Insight agents are not deleted here, as in AI Insight's own list:
 * their configuration and saved dashboards would be left behind.
 */
function useItemPermissions(report: ListedAgent) {
  const standIn = isDefaultStandIn(report);
  const permissions = useEntityResourcePermissions('analyst', standIn ? undefined : report?.aiworker_id);
  if (standIn) return { canRead: true, canUpdate: permissions.canCreate, canDelete: false };
  if (isInsightAgentWorker(report)) return { ...permissions, canDelete: false };
  return permissions;
}

/**
 * Delete. A listed dashboard is taken off All Analysts instead: it becomes private again, so its
 * owner keeps it (AI Insight's `unpublishDashboard`), and its analyst goes with it.
 */
function useDeleteAnalystHandler(report: any, refetchAnalyst: () => void) {
  const { t } = useTranslation();
  const insight = useAnalystInsight();
  const [deleteAiWorkers, { isLoading: deleteAnalystLoading }] = useDeleteAiWorkersMutation();
  const [unpublishing, setUnpublishing] = useState(false);
  const dashboardId = listedDashboardId(report);
  const unpublish = dashboardId ? insight?.unpublishDashboard : undefined;

  const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
    if (unpublish) {
      setUnpublishing(true);
      try {
        await unpublish(dashboardId);
        CtyShowToast(checkType.success, t('{{name}} is no longer listed here. Its owner keeps the dashboard, now private.', { name: report?.name ?? '' }));
        closeModal();
        refetchAnalyst();
      } catch (error) {
        CtyShowToast(checkType.error, (error as Error)?.message || t('It could not be removed. Try again.'));
      } finally {
        setUnpublishing(false);
      }
      return;
    }
    try {
      const response = await deleteAiWorkers(report?.aiworker_id);
      CtyShowToast(checkType.success, response?.data);
      closeModal();
      refetchAnalyst();
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message || 'Delete Operation failed');
    }
  }, [dashboardId, deleteAiWorkers, refetchAnalyst, report?.aiworker_id, report?.name, t, unpublish]);

  return { handleConfirmDelete, isDeleteLoading: deleteAnalystLoading || unpublishing };
}

/**
 * The delete dialog's wording. A listed dashboard is removed, not deleted: its owner keeps it, so the
 * dialog says so and asks for no typed "delete" (sharing it again lists it again).
 */
function useDeleteWording(report: ListedAgent) {
  const { t } = useTranslation();
  if (!isInsightDashboardWorker(report)) return { confirmLabel: t('Delete Analyst') };
  return {
    confirmLabel: t('Remove'),
    menuLabel: t('Remove from All Analysts'),
    buttonLabel: t('Remove'),
    title: t('Remove from All Analysts?'),
    message: (
      <p className="text-sm">
        {t('{{name}} will no longer be listed here, and only its owner can open it. The dashboard itself is kept: its owner can share it again.', { name: report?.name ?? '' })}
      </p>
    ),
    requiresTypedConfirmation: false,
  };
}

/** What kind of analytics agent a card is, when AI Insight is on: Insight agent, or an analyst on the Insight runtime. */
function KindChips({ report, insight }: { readonly report: ListedAgent; readonly insight: AnalystInsight | null }) {
  const { t } = useTranslation();
  if (!insight) return null;
  if (isInsightDashboardWorker(report)) {
    return <Chip size="sm" variant="flat" color="warning">{t('Dashboard')}</Chip>;
  }
  if (isInsightAgentWorker(report)) {
    const isDefault = isDefaultStandIn(report) || (report?.code && report.code === insight.defaultAgentCode);
    return (
      <div className="flex flex-wrap gap-1">
        <Chip size="sm" variant="flat" color="secondary">{t('Insight agent')}</Chip>
        {isDefault ? <Chip size="sm" variant="flat" color="primary">{t('Default')}</Chip> : null}
      </div>
    );
  }
  if (analystRoute(report).runtime === ANALYST_RUNTIMES.INSIGHT) {
    return <Chip size="sm" variant="flat" color="success">{t('Insight runtime')}</Chip>;
  }
  return null;
}

const AnalystCardItem = ({ report, refetchAnalyst }: { readonly report: any; readonly refetchAnalyst: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const insight = useAnalystInsight();
  const { canRead, canUpdate, canDelete } = useItemPermissions(report);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);
  const wording = useDeleteWording(report);

  const footerItems = (
    <>
      <CtyButton className={'w-full border-1'} variant={'bordered'} radius='sm' size={'lg'} color={'default'} isDisabled={!canUpdate} onPress={() => navigate(editUrl(report, insight), navigateState(report))}>
        {isDefaultStandIn(report) ? t('Customise') : t('Edit')}
      </CtyButton>
      <CtyButton className={'w-full'} variant={'solid'} radius='sm' size={'lg'} color={'primary'} isDisabled={!canRead} onPress={() => navigate(launchUrl(report, insight), navigateState(report))}>
        {t('Launch')}
      </CtyButton>
    </>
  );

  return (
    <EntityCardWithActions
      id={report.aiworker_id}
      name={report.name}
      description={report.description}
      bodyExtra={<KindChips report={report} insight={insight} />}
      view={report.category === categoryType.ANALYST}
      footerItems={footerItems}
      canDelete={canDelete}
      dropdownAriaLabel="Analyst Actions"
      deleteModalEntityName={report?.name ?? ''}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={wording.confirmLabel}
      deleteMenuLabel={wording.menuLabel}
      deleteModalTitle={wording.title}
      deleteModalMessage={wording.message}
      deleteRequiresTypedConfirmation={wording.requiresTypedConfirmation}
    />
  );
};

const AnalystActionsCell = ({ report, refetchAnalyst }: { readonly report: any; readonly refetchAnalyst: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const insight = useAnalystInsight();
  const { canRead, canUpdate, canDelete } = useItemPermissions(report);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);
  const wording = useDeleteWording(report);

  return (
    <ListActionsCellWithDelete
      actions={[
        {
          label: isDefaultStandIn(report) ? t('Customise') : t('Edit'),
          onPress: () => navigate(editUrl(report, insight), navigateState(report)),
          variant: 'bordered',
          color: 'default',
          isDisabled: !canUpdate,
        },
        {
          label: t('Launch'),
          onPress: () => navigate(launchUrl(report, insight), navigateState(report)),
          variant: 'solid',
          color: 'primary',
          isDisabled: !canRead,
        },
      ]}
      canDelete={canDelete}
      entityName={report?.name ?? ''}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={wording.confirmLabel}
      deleteButtonLabel={wording.buttonLabel}
      deleteModalTitle={wording.title}
      deleteModalMessage={wording.message}
      deleteRequiresTypedConfirmation={wording.requiresTypedConfirmation}
    />
  );
};

const renderAnalystActionsCell = (refetchAnalyst: () => void) =>
  function AnalystActionsRenderer(params: any) {
    return <AnalystActionsCell report={params?.data} refetchAnalyst={refetchAnalyst} />;
  };

/**
 * Every analytics agent the user can use, for All Analysts. Without AI Insight: the analysts, as
 * before (Insight agents and dashboards need the Insight runtime). With it, also the Insight agents,
 * except those linked from an analyst (the analyst stands for them), the dashboards shared with the
 * tenant, and the default Insight Agent's stand-in until someone customises it.
 */
export function listedAnalyticsAgents<T extends ListedAgent>(
  reports: readonly T[],
  insight: AnalystInsight | null,
  defaultName: string,
  defaultDescription: string
): Array<T | ListedAgent> {
  // A dashboard is listed only where its page can open (AI Insight's dashboardUrl) and it names one.
  const opens = (report: T) => !isInsightDashboardWorker(report) || Boolean(insight?.dashboardUrl && listedDashboardId(report));
  const analysts = reports.filter((report) => report?.category === categoryType.ANALYST && opens(report));
  if (!insight) return analysts.filter((report) => !isInsightAgentWorker(report));
  const linked = linkedInsightAgentIds(analysts);
  const listed = analysts.filter((report) => !(isInsightAgentWorker(report) && linked.has(report.aiworker_id ?? '')));
  const hasDefault = listed.some((report) => isInsightAgentWorker(report) && report.code === insight.defaultAgentCode);
  if (hasDefault) return listed;
  return [...listed, {
    aiworker_id: DEFAULT_INSIGHT_AGENT_ITEM_ID,
    name: defaultName,
    description: defaultDescription,
    category: categoryType.ANALYST,
    sub_type: INSIGHT_AGENT_SUB_TYPE,
    aiworkers_attrs: [],
  }];
}

export function AnalyticsList() {
  const token = useTypedSelector((state) => state.auth.token);
  const [skip, setSkip] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);
  const { data: reports, error, isLoading, isFetching, refetch } = useGetAiWorkersByCategoryQuery({ category: categoryType.ANALYST }, { skip, refetchOnMountOrArgChange: true });
  const { t } = useTranslation();
  const insight = useAnalystInsight();
  useEffect(() => {
    if (token && skip) {
      setSkip(false);
    }
  }, [skip, token]);

  const analystReports = useMemo(
    () => listedAnalyticsAgents(
      reports ?? [],
      insight,
      insight ? t(insight.defaultAgentName) : '',
      insight ? t(insight.defaultAgentDescription) : ''
    ),
    [reports, insight, t]
  );

  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return analystReports;
    return analystReports.filter((report: any) =>
      report.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [analystReports, searchQuery]);

  const columnDefs = useMemo(
    () => [
      getNameColumnDef(t('Name')),
      getDescriptionColumnDef(t('Description')),
      ...(insight ? [{
        headerName: t('Runs on'),
        colId: 'runtime',
        sortable: true,
        filter: false,
        minWidth: 150,
        maxWidth: 200,
        valueGetter: (params: { data?: ListedAgent }) => (
          isInsightAgentWorker(params?.data) || isInsightDashboardWorker(params?.data)
            || analystRoute(params?.data).runtime === ANALYST_RUNTIMES.INSIGHT
            ? t('Insight runtime')
            : t('NL analyst')
        ),
      }] : []),
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 240,
        maxWidth: 280,
        cellRenderer: renderAnalystActionsCell(refetch),
      }),
    ],
    [t, refetch, insight]
  );

  if (analystReports.length === 0 && !isLoading && !isFetching && !error) {
    return (
      <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
        <div className="text-2xl text-primary">
          {t('No Analysts found')}
        </div>
      </div>
    );
  }

  return (
    <EntityListView
      items={filteredReports}
      isLoading={isLoading || isFetching}
      error={error}
      searchId="analysts-search"
      searchPlaceholder={t('Search analysts')}
      onSearch={setSearchQuery}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      emptyMessage={t('No Analysts found')}
      cardKey={(report: any) => report.aiworker_id}
      renderCard={(report: any) => (
        <AnalystCardItem report={report} refetchAnalyst={refetch} />
      )}
      tableId="analysts-list-table"
      columnDefs={columnDefs}
    />
  );
}

export default AnalyticsList;
